/**
 * Save codes: the whole identity of a cloud save. There are no accounts, so whoever holds the code
 * holds the save - it has to be unguessable (130 random bits) and hard to mistype (Crockford
 * base32: no I, L, O or U, case-blind, read aloud safely). One extra character is a checksum, so a
 * typing slip is refused on the device and never becomes a lookup on the server.
 *
 * Canonical form: 27 characters, no hyphens. Shown as `XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XX`.
 * server/saveCode.mjs is the server's copy of the validator; tests/unit/cloudCodes.test.ts holds
 * the two to the same answers.
 */

export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** 26 characters x 5 bits = the 130 random bits. */
const DATA_CHARS = 26;
export const CODE_LENGTH = DATA_CHARS + 1;
/** 130 bits rounded up to whole bytes. */
const RANDOM_BYTES = 17;
const GROUP = 5;

/** What people type for what they read: the letters Crockford leaves out because they look like digits. */
const LOOKALIKES: Readonly<Record<string, string>> = { O: '0', I: '1', L: '1' };

/** Multiplication in GF(32), modulo the primitive polynomial x^5 + x^2 + 1. */
function gfMul(a: number, b: number): number {
  let product = 0;
  let x = a;
  for (let y = b; y > 0; y >>= 1) {
    if ((y & 1) === 1) product ^= x;
    x <<= 1;
    if ((x & 32) !== 0) x ^= 0b100101;
  }
  return product;
}

/**
 * The check value of the data characters: sum of value x weight over GF(32), weights 2^1..2^26.
 * The check character itself carries weight 1. Every weight is different and none is zero, and a
 * field has no zero divisors, so ANY single wrong character and ANY two characters swapped change
 * the sum - which a modulus-32 sum or Luhn cannot promise (Luhn mod 32 misses 0 <-> Z).
 */
function checkValue(values: readonly number[]): number {
  let weight = 1;
  let sum = 0;
  for (const value of values) {
    weight = gfMul(weight, 2);
    sum ^= gfMul(weight, value);
  }
  return sum;
}

/** Upper case, no spaces or dashes, look-alike letters read as the digits they stand for. */
export function normaliseCode(text: string): string {
  const bare = text.toUpperCase().replace(/[\s‐-―_-]/g, '');
  return Array.from(bare, (ch) => LOOKALIKES[ch] ?? ch).join('');
}

/** The code in groups of five, the way it is shown and read out. Accepts anything `normaliseCode` does. */
export function formatCode(code: string): string {
  return normaliseCode(code).match(new RegExp(`.{1,${GROUP}}`, 'g'))?.join('-') ?? '';
}

/** True for a well-formed code with the right checksum, however it was typed. */
export function isValidCode(text: string): boolean {
  const code = normaliseCode(text);
  if (code.length !== CODE_LENGTH) return false;
  const values = Array.from(code, (ch) => CODE_ALPHABET.indexOf(ch));
  if (values.some((v) => v < 0)) return false;
  return checkValue(values.slice(0, DATA_CHARS)) === values[DATA_CHARS];
}

function cryptoBytes(count: number): Uint8Array {
  const bytes = new Uint8Array(count);
  crypto.getRandomValues(bytes);
  return bytes;
}

/** A fresh code in canonical form. `randomBytes` must be a cryptographic source; tests pass a fixed one. */
export function newCode(randomBytes: (count: number) => Uint8Array = cryptoBytes): string {
  const bytes = randomBytes(RANDOM_BYTES);
  if (bytes.length < RANDOM_BYTES) throw new Error('not enough random bytes for a save code');
  const values: number[] = [];
  let pending = 0;
  let bits = 0;
  for (const byte of bytes) {
    pending = (pending << 8) | byte;
    bits += 8;
    while (bits >= 5 && values.length < DATA_CHARS) {
      bits -= 5;
      values.push((pending >> bits) & 31);
    }
    pending &= (1 << bits) - 1;
  }
  values.push(checkValue(values));
  return values.map((v) => CODE_ALPHABET.charAt(v)).join('');
}
